import React, { useState, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Button } from "primereact/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaCamera,
  FaCheck,
  FaEnvelope,
  FaMapMarkerAlt,
  FaRegCircle,
  FaUser,
  FaUserTie,
} from "react-icons/fa";
import { getUserData, getOrgId } from "../utils/authStorage";
import {
  createAgent,
  updateAgentByOrg,
} from "../api/api";
import { useToast } from "../context/ToastContext";
import "../styles/CreateAgentPage.css";

/* ======================
   INITIAL FORM
====================== */
const INITIAL_FORM = {
  name: "",
  email: "",
  region: "",
  fatherName: "",
  cnic: "",
  address: "",
  phoneNumber: "",
  feeValue: "",
  orgID: "",
};

export default function CreateAgent({ mode: modeProp }) {
  const [loading, setLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  const queryMode = new URLSearchParams(location.search).get("mode");
  const mode = queryMode || location.state?.mode || modeProp || "create";
  const initialData = location.state?.user || {};
  const userData = getUserData();
  const user = userData?.user || userData?.data || {};
  const storedOrgId = getOrgId();
  const derivedOrgId = user?.orgId ?? user?.profile?.orgId;
  const orgId = storedOrgId ?? derivedOrgId;

  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  /* ======================
     INITIAL VALUES
  ====================== */
  const formInitialValues = useMemo(() => {
    if (isEditMode || isViewMode) {
      return {
        ...INITIAL_FORM,
        name: initialData.name || "",
        email: initialData.email || "",
        region: initialData.region || "",
        fatherName: initialData.fatherName || "",
        cnic: initialData.cnic || "",
        address: initialData.address || "",
        phoneNumber: initialData.phoneNumber || "",
        feeValue: initialData.feeValue || "",
        iban: initialData.iban || "", // NEW
        accountTitle: initialData.accountTitle || "", // NEW
        orgID: initialData.orgID || orgId || "",
      };
    }
    return { ...INITIAL_FORM, feeValue: "", iban: "", accountTitle: "", orgID: orgId || "" };
  }, [isEditMode, isViewMode, initialData, orgId]);

  /* ======================
     VALIDATION
  ====================== */
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email format").required("Email is required"),
    region: Yup.string().required("Region is required"),

    fatherName: Yup.string().required("Father name is required"),

    cnic: Yup.string()
      .matches(/^[0-9]{5}-[0-9]{7}-[0-9]{1}$/, "Invalid CNIC format")
      .required("CNIC is required"),

    address: Yup.string().required("Address is required"),

    phoneNumber: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Invalid phone number")
      .required("phone number is required"),

    feeValue: Yup.number()
      .typeError("Fee value must be a number")
      .min(0, "Fee value cannot be negative")
      .required("Fee value is required"),
  });

  /* ======================
     SUBMIT
  ====================== */
  const handleSubmit = async (values) => {
    if (isViewMode) return;

    setLoading(true);
    try {
      const payload = {
        ...values,
        orgID: orgId,
      };

      const updatePayload = {
        name: values.name,
        email: values.email,
        region: values.region,
        fatherName: values.fatherName,
        cnic: values.cnic,
        address: values.address,
        phoneNumber: values.phoneNumber,
        iban: values.iban,
        accountTitle: values.accountTitle,
        feeValue: values.feeValue,
      };

      const result = isCreateMode
        ? await createAgent(payload)
        : await updateAgentByOrg(values.cnic, updatePayload);

      if (result?.isSuccess) {
        showSuccess("Success", result.message || "Agent saved successfully");
        navigate("/dashboard/agentslist");
      } else {
        showError("Error", result?.message || "Something went wrong", 3000);
      }
    } catch (err) {
      console.error(err);
      showError("Error", "Error saving agent", 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ca-page">
      <div className="ca-shell">
        <div className="ca-head">
          <h1>{isEditMode ? "Edit Agent" : isViewMode ? "Agent Profile" : "Create New Agent"}</h1>
          <p>Onboard a new high-performance representative to your organization.</p>
        </div>

        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="ca-layout">
              <Field name="orgID" type="hidden" />
              <section className="ca-card ca-card--form">
                <div className="ca-upload">
                  <div className="ca-upload__icon"><FaCamera /></div>
                  <div>
                    <h3>Profile Image</h3>
                    <p>JPG, PNG or SVG. Max size 2MB.</p>
                    <label className="ca-upload__btn" htmlFor="agent-photo">
                      <FaCheck /> Upload Photo
                    </label>
                    <input
                      id="agent-photo"
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={isViewMode}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setPreviewPhoto(URL.createObjectURL(file));
                      }}
                    />
                  </div>
                </div>

                <div className="ca-grid2">
                  <div className="ca-field">
                    <label>Agent Name</label>
                    <div className="ca-input"><FaUser /><Field name="name" as={InputText} placeholder="Enter agent name" disabled={isViewMode} /></div>
                    <ErrorMessage name="name" component="div" className="ca-error" />
                  </div>

                  <div className="ca-field">
                    <label>Region</label>
                    <div className="ca-input"><FaMapMarkerAlt /><Field name="region" as={InputText} placeholder="Select Region" disabled={isViewMode} /></div>
                    <ErrorMessage name="region" component="div" className="ca-error" />
                  </div>
                </div>

                <div className="ca-field">
                  <label>Email Address</label>
                  <div className="ca-input"><FaEnvelope /><Field name="email" as={InputText} placeholder="agent@company.com" disabled={isViewMode} /></div>
                  <ErrorMessage name="email" component="div" className="ca-error" />
                </div>

                <div className="ca-field">
                  <label>Phone Number</label>
                  <div className="ca-phone-row">
                    <span className="ca-prefix">PKR +92</span>
                    <PhoneInput
                      country="pk"
                      value={values.phoneNumber}
                      onChange={(value) => setFieldValue("phoneNumber", value.replace(/\D/g, ""))}
                      inputClass="ca-phone-input"
                      countryCodeEditable={false}
                      disabled={isViewMode || isEditMode}
                    />
                  </div>
                  <ErrorMessage name="phoneNumber" component="div" className="ca-error" />
                </div>

                <div className="ca-grid2">
                  <div className="ca-field">
                    <label>Father Name</label>
                    <div className="ca-input"><FaUserTie /><Field name="fatherName" as={InputText} placeholder="Father Name" disabled={isViewMode} /></div>
                    <ErrorMessage name="fatherName" component="div" className="ca-error" />
                  </div>

                  <div className="ca-field">
                    <label>CNIC</label>
                    <div className="ca-input"><FaRegCircle /><Field
                      name="cnic"
                      as={InputText}
                      placeholder="00000-0000000-0"
                      disabled={isViewMode || isEditMode}
                      maxLength={15}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, "");
                        if (value.length > 5) value = value.slice(0, 5) + "-" + value.slice(5);
                        if (value.length > 13) value = value.slice(0, 13) + "-" + value.slice(13, 14);
                        setFieldValue("cnic", value);
                      }}
                    /></div>
                    <ErrorMessage name="cnic" component="div" className="ca-error" />
                  </div>
                </div>

                <div className="ca-grid2">
                  <div className="ca-field">
                    <label>IBAN</label>
                    <div className="ca-input"><FaRegCircle /><Field name="iban" as={InputText} placeholder="PK00ABCD123456789" disabled={isViewMode} /></div>
                  </div>

                  <div className="ca-field">
                    <label>Account Title</label>
                    <div className="ca-input"><FaUserTie /><Field name="accountTitle" as={InputText} placeholder="Account Title" disabled={isViewMode} /></div>
                  </div>
                </div>

                <div className="ca-grid2">
                  <div className="ca-field">
                    <label>Fee Value</label>
                    <div className="ca-input"><FaRegCircle /><Field name="feeValue" as={InputText} placeholder="Enter fee value" disabled={isViewMode} /></div>
                    <ErrorMessage name="feeValue" component="div" className="ca-error" />
                  </div>

                  <div className="ca-field">
                    <label>Address</label>
                    <div className="ca-input"><FaMapMarkerAlt /><Field name="address" as={InputText} placeholder="Address" disabled={isViewMode} /></div>
                    <ErrorMessage name="address" component="div" className="ca-error" />
                  </div>
                </div>

                <div className="ca-actions">
                  <button type="button" className="ca-btn ca-btn--ghost" onClick={() => navigate("/dashboard/agentslist")}>
                    Cancel
                  </button>
                  {!isViewMode && (
                    <>
                      <button type="button" className="ca-btn" disabled={loading}>Save Draft</button>
                      <Button
                        type="submit"
                        label={
                          loading
                            ? "Saving..."
                            : isEditMode
                              ? "Update Agent"
                              : "Create Agent"
                        }
                        disabled={loading}
                        className="ca-btn ca-btn--primary"
                      />
                    </>
                  )}
                </div>
              </section>

              <aside className="ca-card ca-card--preview">
                <h4>Live Agent Preview</h4>
                <div className="ca-preview-avatar">
                  {previewPhoto ? <img src={previewPhoto} alt="preview" /> : (values.name?.[0]?.toUpperCase() || "N")}
                </div>
                <div className="ca-preview-name">{values.name || "New Agent"}</div>
                <div className="ca-preview-email">{values.email || "No email provided"}</div>

                <div className="ca-preview-meta">
                  <div><span>Status</span><b>Provisioning</b></div>
                  <div><span>Organization</span><b>Main Org</b></div>
                  <div><span>Auth Level</span><b>Tier 1 Support</b></div>
                </div>

                <div className="ca-quota">
                  <span>Monthly Quota</span>
                  <strong>Rs 0 / Rs 0</strong>
                </div>

                <div className="ca-next">
                  <span>Next Action</span>
                  <p>Proceed to <b>Assignment</b> to define territory and permissions.</p>
                </div>
              </aside>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
